"use strict";

// ---------------------------------------------------
// Load Super Class

var SoaModel = require('bunsen/models/SoaModel');

// ---------------------------------------------------
// Module Definition

/**
 * @class ShippingCost
 * @extends SoaModel
 * @property {string} COST_TYPE_UNKNOWN
 */
var ShippingCost = module.exports = SoaModel.extend();

// ---------------------------------------------------
// Dependencies

var Party = require('bunsen/models/Party');
var _ = require('underscore');
var Backbone = require('dibs-backbone');
var FinancialAmount = require('bunsen/models/FinancialAmount');
var financialAmountMixin = require('bunsen/models/mixins/financialAmountMixin');
var Seller = require('bunsen/models/Seller');
var User = require('bunsen/models/identity/User');
var Permission = require('bunsen/models/identity/Permission');
var ShippingCosts = require('bunsen/collections/logistics/ShippingCosts');
var Carriers = require('bunsen/collections/logistics/Carriers');
var Carrier = require('bunsen/models/logistics/Carrier');
var shippingCostTypeDisplay = require('dibs-static/admin/json/common/1/shippingCostTypeDisplay');
var getDeferredMethods = require('bunsen/helpers/getDeferredMethods');
var dataProvider = require('bunsen/helpers/dataProvider');
var globalCache = require('bunsen/helpers/globalCache');

// ---------------------------------------------------
// Local Vars

var deferred = getDeferredMethods().Deferred;
var displayMapCacheKey = Symbol('fetching_cost_type_display_map');

function createPartyFromCurrentUser() {
    var user = Seller.getCurrentSeller();
    if (user) {
        return new Party({id: user.id, role: Party.ROLE_SELLER});
    } else {
        user = User.getCurrentUserSV();
        if (user && user.hasPermission(Permission.PERMISSION_BASIC_INTERNAL)) {
            return new Party({id: user.id, role: Party.ROLE_INTERNAL});
        }
    }
    return null;
}

// ---------------------------------------------------
// Define Module Instance Properties

_.extend(ShippingCost.prototype, /** @lends ShippingCost.prototype */{

    relations: [
        { key: 'billTo', relatedModel: Party, type: Backbone.One },
        { key: 'author', relatedModel: Party, type: Backbone.One },
        { key: 'subTotalAmount', relatedModel: FinancialAmount, type: Backbone.One },
        { key: 'totalAmount', relatedModel: FinancialAmount, type: Backbone.One }
    ],

    /**
     * @returns {boolean}
     */
    isLegacy: function () {
        return this.get('isLegacy');
    },

    /**
     * @returns {boolean}
     */
    isUnknownCostType: function () {
        return this.isCostType(ShippingCost.COST_TYPE_UNKNOWN);
    },

    /**
     * @returns {boolean}
     */
    isTransitCostType: function () {
        return this.isCostType(ShippingCost.COST_TYPE_TRANSIT);
    },

    /**
     * @param {string} costType
     * @returns {boolean}
     */
    isCostType: function (costType) {
        return this.getCostType() === costType;
    },

    /**
     * @param {string} costType
     */
    setCostType: function (costType) {
        this.set('costType', costType);
        return this;
    },

    /**
     * @returns {string}
     */
    getCostType: function () {
        return this.get('costType');
    },

    /**
     * @returns {string|number}
     */
    getBillToId: function () {
        return this.get('billTo.id');
    },

    /**
     * @returns {string}
     */
    getBillToRole: function () {
        return this.get('billTo.role');
    },

    /**
     * @returns {boolean}
     */
    billToIsLSP: function () {
        return this.getBillToRole() === Party.ROLE_LSP;
    },

    /**
     * @returns {number|string}
     */
    getAuthorId: function () {
        return this.get('author.id');
    },

    /**
     * @returns {Party}
     */
    getAuthor: function () {
        return this.get('author');
    },

    /**
     * @returns {Party}
     */
    getBillTo: function () {
        return this.get('billTo');
    },

    /**
     * @returns {SoaModel}
     */
    getBillToEntity: function () {
        return this.get('billTo.entity');
    },

    /**
     * @returns {Carrier}
     */
    getBillToCarrierEntity: function () {
        if (this._billToCarrier && this.getBillToId() === this._billToCarrier.id) {
            return this._billToCarrier;
        }
        var entity = this.getBillToEntity();
        this._billToCarrier = entity && this.billToIsLSP() ? new Carrier(entity.toJSON()) : null;
        return this._billToCarrier;
    },

    /**
     * Looks for inflated carrier entity in the billTo
     * model, and returns it if present. Otherwise, searches
     * the Carriers singleton for a carrier matching the
     * billTo id (if billTo is LSP) and returns that if found.
     * @returns {Carrier}
     */
    findBillToCarrier: function () {
        if (!this.billToIsLSP()) {
            return null;
        }
        var entity = this.getBillToCarrierEntity();
        var id;
        if (entity) {
            return entity;
        }
        id = this.getBillToId();
        return id ? Carriers.findCarrierForId(id) : null;
    },

    /**
     * Returns true if the cost has one of the "default"
     * cost types (which at this point is TRANSIT or UNKNOWN)
     * @returns {boolean}
     */
    isDefaultCost: function () {
        return this.isUnknownCostType() || this.isTransitCostType();
    },

    /**
     * First, searches the billTo object for an inflated
     * entity, and returns the entity name if found. Then,
     * searches the Carriers singleton for a carrier with
     * the matching id of the shipping cost instance's
     * billTo and returns the name.
     * @returns {string}
     */
    findBillToCarrierName: function () {
        var carrier = this.findBillToCarrier();
        return carrier ? carrier.get('name') : null;
    },

    /**
     * Searches the billTo object for in inflated carrier
     * entity, and returns the name if found. Otherwise,
     * searches the Carriers singleton for a matching carrier
     * id and returns that match's code.
     * @returns {string}
     */
    findBillToCarrierCode: function () {
        var carrier = this.findBillToCarrier();
        return carrier ? carrier.get('code') : null;
    },

    /**
     * Attempts to find the bill to Carrier model. If found,
     * then returns a formatted version of the carrier name.
     * Otherwise, returns null.
     * @returns {string}
     */
    describeBillToCarrierName: function () {
        var carrier = this.findBillToCarrier();
        return carrier ? carrier.describeCarrierName() : null;
    },

    /**
     * Returns true if the billTo has an inflated
     * carrier entity and that carrier entity is
     * an "other" custom carrier.
     * @returns {boolean}
     */
    billToCarrierIsOther: function () {
        var carrier = this.findBillToCarrier();
        return carrier ? carrier.isOtherCarrier() : false;
    },

    /**
     * @returns {string}
     */
    getCostTypeDisplayVal: function () {
        return ShippingCost.getDisplayValueForCostType(this.getCostType());
    },

    /**
     * @param {Party|object} author
     */
    setAuthor: function (author) {
        this.set('author', author instanceof Party ? author.toJSON() : author);
        return this;
    },

    /**
     * @param {Party|object} billTo
     */
    setBillTo: function (billTo) {
        billTo = billTo instanceof Party ? billTo.toJSON() : billTo;
        var model = this.get('billTo');
        if (model) {
            model.clear({ silent: true }).set(billTo);
        } else {
            this.set('billTo', billTo);
        }
        return this;
    },

    /**
     * @returns {FinancialAmount}
     */
    getSubTotalAmount: function () {
        return this.get('subTotalAmount');
    },

    /**
     * @returns {string|number}
     */
    getSubTotalNum: function () {
        return this.get('subTotalAmount.amount');
    },

    /**
     * @returns {string}
     */
    getCurrency: function () {
        return this.get('subTotalAmount.currency');
    },

    /**
     * @param {number} subTotal
     * @returns {ShippingCost}
     */
    setSubTotal: function (subTotal) {
        var amount = this._ensureSubTotalAmount();
        amount.set('amount', subTotal);
        return this;
    },

    /**
     * @param {number} subTotal
     * @param {string} currency
     * @returns {ShippingCost}
     */
    setSubTotalAndCurr: function (subTotal, currency) {
        var amount = this._ensureSubTotalAmount();
        amount.set({
            amount: subTotal,
            currency: currency
        });
        return this;
    },

    /**
     * @returns {FinancialAmount}
     * @private
     */
    _ensureSubTotalAmount: function () {
        var amount = this.getSubTotalAmount();
        if (!amount) {
            this.set('subTotalAmount', {});
            return this.getSubTotalAmount();
        }
        return amount;
    }


}, financialAmountMixin);

// ---------------------------------------------------
// Define Module Static Properties

_.extend(ShippingCost, /** @lends ShippingCost */{

    costTypeDisplayMap: null,

    COST_TYPE_UNKNOWN: 'UNKNOWN',

    COST_TYPE_TRANSIT: 'TRANSIT',

    /**
     * @public
     * @static
     * @param {string} costType
     * @param {string} authorRole
     * @returns {{role:string,id:number}}
     */
    getDefaultBillToForType: function (costType, authorRole) {
        if (costType && authorRole) {
            var defaultCosts = authorRole === Party.ROLE_INTERNAL ?
                ShippingCosts.adminDefaultCostsInstance() : ShippingCosts.sellerDefaultCostsInstance();
            var defaultBillTo = defaultCosts.getBillToForType(costType);
            return defaultBillTo ? defaultBillTo.toJSON() : null;
        }
        return _.indexOf([ShippingCost.COST_TYPE_UNKNOWN, ShippingCost.COST_TYPE_TRANSIT], costType) > -1 ? { role: Party.ROLE_LSP } : null;
    },

    /**
     * @public
     * @static
     * @memberOf ShippingCost
     * @param {string} [costType]
     * @param {number} [subTotal]
     * @param {string} [currency]
     * @returns {ShippingCost}
     */
    createWithTypeAndSubTotal : function (costType, subTotal, currency) {
        return new ShippingCost({
            costType: costType,
            subTotalAmount: {
                amount: subTotal,
                currency: currency
            }
        });
    },

    /**
     * Creates a new ShippingCost based on a simplified
     * options parameter. Defaults author to the be based
     * on the current seller or internal user. Defaults the
     * billTo to use the billTo derived from the
     * ShippingCosts defaults instance.
     * @public
     * @static
     * @param {object} [config]
     * @param {object} [config.author]
     * @param {object} [config.billTo]
     * @param {string} [config.costType]
     * @param {boolean} [config.isLegacy]
     * @param {number} [config.subTotal]
     * @param {string} [config.currency]
     * @returns {ShippingCost}
     */
    createWithConfig: function (config) {
        var authorRole;
        config = config ? _.clone(config) : {};
        config.author = config.author || createPartyFromCurrentUser();
        authorRole = config.author ? config.author.role : null;
        config.billTo = config.billTo || ShippingCost.getDefaultBillToForType(config.costType, authorRole);
        return new ShippingCost({
            costType: config.costType,
            author: config.author || {},
            billTo: config.billTo || {},
            isLegacy: config.isLegacy,
            subTotalAmount: {
                amount: config.subTotal,
                currency: config.currency
            }
        });
    },

    /**
     * @public
     * @static
     * @param {number} subTotal
     * @param {string} currency
     * @returns {ShippingCost}
     */
    createUnknownCostWithSubTotal : function (subTotal, currency) {
        return ShippingCost.createWithConfig({
            costType: ShippingCost.COST_TYPE_UNKNOWN,
            subTotal:  subTotal,
            currency: currency,
            isLegacy: true
        });
    },

    /**
     * Fetches the json map for the cost type display values.
     * Defers to the data provider value for the map, but if
     * the data provider returns an empty result, then the json
     * is fetched from a url to the static repo.
     * @public
     * @static
     * @param {string} dataProviderKey
     * @returns {Deferred.promise}
     */
    requestCostTypeDisplayMapUseDP: function (dataProviderKey) {
        var costTypeMapProm = globalCache.get(displayMapCacheKey);
        if (costTypeMapProm) {
            return costTypeMapProm;
        }
        var data = dataProvider.get(dataProviderKey || 'shipping_cost_type_display');
        if (data) {
            costTypeMapProm = deferred().resolve(data).promise();
        } else {
            costTypeMapProm = deferred().resolve(shippingCostTypeDisplay).promise();
        }
        costTypeMapProm.done(function (data) {
            ShippingCost.costTypeDisplayMap = data;
            globalCache.set(displayMapCacheKey, costTypeMapProm);
        });

        return costTypeMapProm;
    },

    /**
     * @param {string} costType
     * @returns {string}
     */
    getDisplayValueForCostType: function (costType) {
        var map = ShippingCost.costTypeDisplayMap;
        return map ? map[costType] : null;
    }

});



// WEBPACK FOOTER //
// ./~/bunsen/models/logistics/ShippingCost.es.js